import { useEffect, useRef, type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgendaItemKind, AgendaItemStatus } from '@up2d8/contracts';
import { cn, dayOf, monthGrid } from '@up2d8/ui';
import { addDays, minutesOnDay, weekDays, type CalendarView } from '../../shared/calendar-range';
import { uiLocale } from '../../shared/format';
import './agenda-calendar.css';

/**
 * De agenda als kalender (FBK-9): dag, week en maand, zoals in Outlook.
 *
 * Eén component voor het beheer en de app, met een eigen stijlblad op de tokens, zodat het in
 * beide zones dezelfde kalender is. Het component haalt niets op: de pagina geeft de evenementen
 * van de zichtbare periode mee, en beslist wat een klik op een evenement of een leeg vak doet.
 */

export interface CalendarItem {
  id: string;
  title: string;
  kind: AgendaItemKind;
  status: AgendaItemStatus;
  startsAt: string;
  endsAt: string;
  location: string;
}

/** De zichtbare uren in week en dag; vroeger en later scrol je naartoe. */
const HOUR_PX = 40;
const FIRST_VISIBLE_HOUR = 6;

export function AgendaCalendar({
  view,
  anchor,
  items,
  onPickDay,
  onOpenItem,
  onCreateAt,
}: {
  view: CalendarView;
  /** De dag waar de weergave om draait, "2026-10-28". */
  anchor: string;
  items: readonly CalendarItem[];
  /** Een klik op een dagnummer: naar de dagweergave. */
  onPickDay: (day: string) => void;
  onOpenItem?: (item: CalendarItem) => void;
  /** Een klik op een leeg vak in dag of week: nieuw evenement op dat uur. Zonder: alleen lezen. */
  onCreateAt?: (day: string, hour: number) => void;
}) {
  if (view === 'month') {
    return (
      <MonthView anchor={anchor} items={items} onPickDay={onPickDay} onOpenItem={onOpenItem} />
    );
  }
  const days = view === 'week' ? weekDays(anchor) : [anchor];
  return (
    <TimeView
      days={days}
      items={items}
      onPickDay={onPickDay}
      onOpenItem={onOpenItem}
      onCreateAt={onCreateAt}
    />
  );
}

/** Wat er op een dag staat, op begintijd. */
function itemsOn(day: string, items: readonly CalendarItem[]): CalendarItem[] {
  return items
    .filter((item) => minutesOnDay(day, new Date(item.startsAt), new Date(item.endsAt)) !== null)
    .sort((left, right) => left.startsAt.localeCompare(right.startsAt));
}

function timeOf(iso: string, locale: string): string {
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

function weekdayLabels(locale: string): string[] {
  // Een maandag als vertrekpunt; de namen komen van de browser, in de taal van de lezer.
  return weekDays('2026-10-26').map((day) =>
    new Date(`${day}T12:00:00`).toLocaleDateString(locale, { weekday: 'short' }),
  );
}

function MonthView({
  anchor,
  items,
  onPickDay,
  onOpenItem,
}: {
  anchor: string;
  items: readonly CalendarItem[];
  onPickDay: (day: string) => void;
  onOpenItem?: (item: CalendarItem) => void;
}) {
  const { t } = useTranslation();
  const locale = uiLocale();
  const today = dayOf(new Date());
  const cells = monthGrid(anchor.slice(0, 7));
  const grid = useRef<HTMLDivElement>(null);

  // Pijltjestoetsen tussen de dagnummers: een dag links, rechts, een week op of neer.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    const delta = moves[event.key];
    const current = (event.target as HTMLElement).dataset.day;
    if (delta === undefined || !current) return;
    const next = grid.current?.querySelector<HTMLButtonElement>(
      `[data-day="${addDays(current, delta)}"]`,
    );
    if (next) {
      event.preventDefault();
      next.focus();
    }
  };

  return (
    <div className="cal cal-month" data-testid="agenda-calendar-month">
      <div className="cal-month-head" aria-hidden="true">
        {weekdayLabels(locale).map((label) => (
          <div key={label}>{label}</div>
        ))}
      </div>
      <div className="cal-month-grid" ref={grid} onKeyDown={onKeyDown}>
        {cells.map((cell) => {
          const onDay = itemsOn(cell.date, items);
          return (
            <div
              key={cell.date}
              className={cn('cal-cell', !cell.inMonth && 'out', cell.date === today && 'today')}
            >
              <button
                type="button"
                className="cal-daynum"
                data-day={cell.date}
                data-testid="calendar-day"
                aria-label={`${new Date(`${cell.date}T12:00:00`).toLocaleDateString(locale, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}${onDay.length ? `, ${t('agendaCalendar.count', { count: onDay.length })}` : ''}`}
                onClick={() => onPickDay(cell.date)}
              >
                {cell.day}
              </button>
              {onDay.slice(0, 3).map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={cn('cal-chip', `k-${item.kind}`, item.status === 'draft' && 'draft')}
                  title={`${timeOf(item.startsAt, locale)} ${item.title}`}
                  data-testid="calendar-item"
                  onClick={() => (onOpenItem ? onOpenItem(item) : onPickDay(cell.date))}
                >
                  <span className="cal-chip-time">{timeOf(item.startsAt, locale)}</span>
                  {item.title}
                </button>
              ))}
              {onDay.length > 3 ? (
                <button type="button" className="cal-more" onClick={() => onPickDay(cell.date)}>
                  {t('agendaCalendar.more', { count: onDay.length - 3 })}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TimeView({
  days,
  items,
  onPickDay,
  onOpenItem,
  onCreateAt,
}: {
  days: string[];
  items: readonly CalendarItem[];
  onPickDay: (day: string) => void;
  onOpenItem?: (item: CalendarItem) => void;
  onCreateAt?: (day: string, hour: number) => void;
}) {
  const { t } = useTranslation();
  const locale = uiLocale();
  const today = dayOf(new Date());
  const scroller = useRef<HTMLDivElement>(null);

  // Bij openen op 06:00: daar begint de werkdag in een fabriek, en de nacht staat erboven.
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = FIRST_VISIBLE_HOUR * HOUR_PX;
  }, []);

  const createAt = (day: string) => (event: MouseEvent<HTMLDivElement>) => {
    if (!onCreateAt || event.target !== event.currentTarget) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const hour = Math.min(23, Math.max(0, Math.floor((event.clientY - rect.top) / HOUR_PX)));
    onCreateAt(day, hour);
  };

  return (
    <div
      className={cn('cal cal-time', days.length === 1 && 'single')}
      data-testid={days.length === 1 ? 'agenda-calendar-day' : 'agenda-calendar-week'}
      style={{ ['--cal-days' as string]: days.length }}
    >
      <div className="cal-time-head">
        <div />
        {days.map((day) => (
          <button
            key={day}
            type="button"
            className={cn('cal-time-day', day === today && 'today')}
            onClick={() => onPickDay(day)}
          >
            {new Date(`${day}T12:00:00`).toLocaleDateString(locale, {
              weekday: 'short',
              day: 'numeric',
              month: days.length === 1 ? 'long' : 'numeric',
            })}
          </button>
        ))}
      </div>
      <div className="cal-time-body" ref={scroller}>
        <div className="cal-hours" aria-hidden="true">
          {Array.from({ length: 24 }, (_, hour) => (
            <div key={hour} style={{ height: HOUR_PX }}>
              {`${hour < 10 ? '0' : ''}${hour}:00`}
            </div>
          ))}
        </div>
        {days.map((day) => (
          <div
            key={day}
            className={cn('cal-column', onCreateAt && 'creatable')}
            style={{ height: 24 * HOUR_PX }}
            onClick={createAt(day)}
            title={onCreateAt ? t('agendaCalendar.clickToCreate') : undefined}
            data-testid="calendar-column"
          >
            {itemsOn(day, items).map((item) => {
              const span = minutesOnDay(day, new Date(item.startsAt), new Date(item.endsAt));
              if (!span) return null;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={cn('cal-block', `k-${item.kind}`, item.status === 'draft' && 'draft')}
                  style={{
                    top: (span.from / 60) * HOUR_PX,
                    height: Math.max(20, ((span.to - span.from) / 60) * HOUR_PX - 2),
                  }}
                  data-testid="calendar-item"
                  onClick={() => onOpenItem?.(item)}
                >
                  <strong>{item.title}</strong>
                  <span>
                    {timeOf(item.startsAt, locale)}–{timeOf(item.endsAt, locale)}
                    {item.location ? ` · ${item.location}` : ''}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export type AgendaView = 'list' | CalendarView;

const VIEW_KEY = 'up2d8.agenda-view';

/** De laatst gekozen weergave, per toestel; zonder opslag (privévenster) gewoon de lijst. */
export function rememberedView(): AgendaView {
  try {
    const value = window.localStorage.getItem(VIEW_KEY);
    return value === 'day' || value === 'week' || value === 'month' ? value : 'list';
  } catch {
    return 'list';
  }
}

export function rememberView(view: AgendaView): void {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch {
    // Geen opslag: dan onthouden we het niet, en dat is alles.
  }
}

/** Lijst · Dag · Week · Maand, en bladeren met vorige, vandaag en volgende. */
export function CalendarToolbar({
  view,
  onView,
  title,
  onPrevious,
  onToday,
  onNext,
}: {
  view: AgendaView;
  onView: (view: AgendaView) => void;
  /** "oktober 2026", "26 okt – 1 nov" of "woensdag 28 oktober". */
  title: string;
  onPrevious: () => void;
  onToday: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="cal-toolbar" data-testid="agenda-calendar-toolbar">
      <div className="cal-views" role="radiogroup" aria-label={t('agendaCalendar.view')}>
        {(['list', 'day', 'week', 'month'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={view === option}
            className={cn(view === option && 'on')}
            data-testid={`agenda-view-${option}`}
            onClick={() => onView(option)}
          >
            {t(`agendaCalendar.views.${option}`)}
          </button>
        ))}
      </div>
      {view === 'list' ? null : (
        <div className="cal-nav">
          <button type="button" onClick={onPrevious} aria-label={t('agendaCalendar.previous')}>
            ‹
          </button>
          <button type="button" className="cal-today" onClick={onToday}>
            {t('agendaCalendar.today')}
          </button>
          <button type="button" onClick={onNext} aria-label={t('agendaCalendar.next')}>
            ›
          </button>
          <span className="cal-title" data-testid="agenda-calendar-title">
            {title}
          </span>
        </div>
      )}
    </div>
  );
}

/** De titel boven de kalender, in de taal van de lezer. */
export function calendarTitle(view: CalendarView, anchor: string, locale: string): string {
  const noon = (day: string) => new Date(`${day}T12:00:00`);
  if (view === 'month') {
    return noon(anchor).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }
  if (view === 'day') {
    return noon(anchor).toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }
  const days = weekDays(anchor);
  const short = { day: 'numeric', month: 'short' } as const;
  return `${noon(days[0] as string).toLocaleDateString(locale, short)} – ${noon(
    days[6] as string,
  ).toLocaleDateString(locale, short)}`;
}
